import React, { useContext, useState } from 'react';
import { Memo, useMemoService } from '../../../sdk/MemoServiceClient';
import { useAsyncEffect } from '@app/hooks';
import { AccountContext } from '../AccountContext';
import { toFormattedDate } from '@app/utilities';
import { AddEditMemo } from './AddEditMemo';
import { useGlobalContext } from '../../../GlobalContextProvider';
import { ContentHeader, Table, Error } from '@app/shared';
import { ColDef } from 'ag-grid-community';
import { Segment, Divider } from 'semantic-ui-react';
import { ComponentErrorBoundary } from '@app/utilities';
import { MasterDetailLayout } from '../components/MasterDetailLayout';

export function AccountMemos() {
    const [grid, setGrid] = React.useState(() => { });
    const { accountId } = useContext(AccountContext);
    const { tenantId } = useGlobalContext();
    const memoService = useMemoService(tenantId);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [memos, setMemos] = useState<Memo[]>([]);
    const [memoAdded, setMemoAdded] = useState<number>(1);
    const [filtersCleared, setFiltersCleared] = useState<number>(1);
    const [createdByFilter, setCreatedByFilter] = useState<string>(undefined);
    const [channelFilter, setChannelFilter] = useState<string>(undefined);
    const [categoryCodeFilter, setCategoryCodeFilter] = useState<string>(undefined);
    const [startDateFilter, setStartDateFilter] = useState<string>(undefined);
    const [endDateFilter, setEndDateFilter] = useState<string>(undefined);
    const [error, setError] = useState<string | undefined>(undefined);

    const columnDefs: ColDef[] = [
        { field: 'checkboxBtn', headerName: '', checkboxSelection: true, headerCheckboxSelection: true, pinned: 'left', width: 5, suppressHeaderFilterButton : true, resizable: false},
        { field: 'note', headerName: 'Note' },
        { field: 'categoryCode', headerName: 'Category Code' },
        { field: 'createdDate', headerName: 'Created', valueFormatter: (params) => toFormattedDate(params.value) },
        { field: 'createdBy', headerName: 'Created By' },
        { field: 'channel', headerName: 'Channel' }
    ];

    useAsyncEffect(async () => {
        await reloadMemos();
    }, [accountId, memoAdded, filtersCleared]);

    function onMemoAdded() {
        setMemoAdded((memoAdded) => memoAdded + 1);
    }
    async function onFilterMemos(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        await reloadMemos();
    }

    async function reloadMemos() {
        try {
            setIsLoading(true);
            const memoResponse = await memoService.getAccountMemos(accountId, channelFilter, categoryCodeFilter, createdByFilter, startDateFilter, endDateFilter);
            setIsLoading(false);
            setMemos(memoResponse?.memos);
        } catch (error) {
            setIsLoading(false);
            setError(error);
        }
    }

    function resetFilters() {
        setCategoryCodeFilter('');
        setCreatedByFilter('');
        setChannelFilter('');
        setStartDateFilter('');
        setEndDateFilter('');
        setFiltersCleared((filtersCleared) => filtersCleared + 1);
    }

    async function deleteMemo() {
        const rows = grid.getSelectedRows();
        if (rows && rows.length != 1) {
            alert("No account memo selected for deletion.");
            return;
        }
        if (confirm("Are you sure you want to delete the selected account memo?")) {
            try {
                setIsLoading(true);
                await memoService.deleteAccountMemo(accountId, rows[0].memoId);
                setIsLoading(false);
                await reloadMemos();
            } catch (error) {
                setIsLoading(false);
                setError(error);
            }
        }
    }

    const onGridReady = (params) => {
        setGrid(params.api);
    }

    const master = (
        <>
            <ContentHeader>
                Account Memos
            </ContentHeader>
            <Divider horizontal>Server Side Filtering</Divider>
            <div>
                <ComponentErrorBoundary>
                    <form className="ui form" onSubmit={(e) => onFilterMemos(e)}>
                        <Error error={error} />
                        <div className="field">
                            <div className="ui labeled input">
                                <div className="ui label">Category Code</div>
                                <input
                                    type="text"
                                    value={categoryCodeFilter || ''}
                                    onChange={(e) => setCategoryCodeFilter(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>
                        </div>
                        <div className="field">
                            <div className="ui labeled input">
                                <div className="ui label">Channel</div>
                                <input
                                    type="text"
                                    value={channelFilter || ''}
                                    onChange={(e) => setChannelFilter(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>
                        </div>
                        <div className="field">
                            <div className="ui labeled input">
                                <div className="ui label">Created By</div>
                                <input
                                    type="text"
                                    value={createdByFilter || ''}
                                    onChange={(e) => setCreatedByFilter(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>
                        </div>
                        <div className="field">
                            <div className="ui labeled input">
                                <div className="ui label">Start Date</div>
                                <input
                                    type="date"
                                    value={startDateFilter || ''}
                                    onChange={(e) => setStartDateFilter(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>
                        </div>
                        <div className="field">
                            <div className="ui labeled input">
                                <div className="ui label">End Date</div>
                                <input
                                    type="date"
                                    value={endDateFilter || ''}
                                    onChange={(e) => setEndDateFilter(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>
                        </div>
                        <div className="field">
                            <button className={`ui button ${isLoading && 'loading'}`} type="submit">
                                <span>Filter Memos</span>
                            </button>
                            <button className={`ui button ${isLoading && 'loading'}`} type="button" onClick={(e) => resetFilters()}>
                                <span>Reset</span>
                            </button>
                        </div>
                    </form>
                </ComponentErrorBoundary>
            </div>
            <Divider horizontal>Memos</Divider>
            <div>
                <button className={`ui button ${isLoading && 'loading'}`} type="button" onClick={_ => { deleteMemo() }}>
                    <span>Delete memo</span>
                </button>
            </div>
            <div style={{ height: 'calc(100% - 100px)' }}>
                <Table items={memos ? memos : []} columnDefs={columnDefs} noFilter={true} onGridReady={onGridReady} />
            </div>
        </>
    );

    const detail = (
        <Segment>
            <ContentHeader>Add Account Memo</ContentHeader>
            <AddEditMemo accountId={accountId} onMemoAdded={onMemoAdded} />
        </Segment>
    );

    return (
        <ComponentErrorBoundary>
            <MasterDetailLayout master={master} detail={detail} />
        </ComponentErrorBoundary>
    );
}
